import type { HTMLAttributes } from 'react'
import { Spinner } from './Spinner'
import { cx } from './cx'

export interface ProgressPanelProps extends HTMLAttributes<HTMLDivElement> {
  /** Bold headline, e.g. "Analyzing your files" / "Uploading your files". */
  readonly title: string
  /** Muted supporting line under the title — can be static copy or a live count. */
  readonly subtitle: string
  /** 0-100. Rounded for display and for `aria-valuenow`; callers may pass a
   * real, measured value or an openly-simulated one — this component makes
   * no claim either way. */
  readonly pct: number
  /** `aria-label` for the progressbar itself (distinct from the panel's own
   * `aria-label`, which callers pass through `rest` when the panel doubles
   * as a page-level busy region — see `StagingPage.tsx`). */
  readonly progressLabel: string
}

/**
 * UID-550 — extracted from `StagingPage.tsx`'s "Analyzing your files" loading
 * state (spinner + bold title + muted subtitle + a 320px pill track/fill bar
 * + a right-aligned `%`) so a SECOND moment that also waits on real work —
 * the upload batch itself — could reuse the exact same visual pattern instead
 * of a copy-pasted, slightly-different one.
 *
 * UID-583 — that second caller (`UploadFileList.tsx`) is GONE: ADR-0173
 * removed the on-page upload panel and moved the wait onto the staging
 * screen, so `StagingPage` is now the only consumer. The extraction is kept
 * rather than inlined: it is a clean primitive, and the removal was a
 * product decision about WHERE the wait is shown, not a judgement about this
 * component.
 *
 * Deliberately agnostic about whether `pct` is real or simulated, and that
 * still matters with one caller — `StagingPage` now feeds it BOTH: the real,
 * byte-weighted upload percentage while the bytes move, then its
 * openly-animated simulated ramp for the analysis phase, which has no
 * backend progress signal at all (ADR-0134 R2).
 *
 * `...rest` (via `HTMLAttributes<HTMLDivElement>`) lets a caller that uses
 * this panel as a page-level busy region (like `StagingPage`) attach
 * `aria-busy`/`aria-live`/`aria-label` to the SAME div this component
 * renders, rather than needing a second wrapping element around it.
 */
export function ProgressPanel({
  title,
  subtitle,
  pct,
  progressLabel,
  className,
  ...rest
}: ProgressPanelProps) {
  const rounded = Math.round(pct)
  return (
    <div className={cx('progress-panel', className)} {...rest}>
      <Spinner size="lg" aria-hidden="true" />
      <p className="progress-panel-title">{title}</p>
      <p className="caption-text">{subtitle}</p>
      {/* biome-ignore lint/a11y/useFocusableInteractive: purely informational — nothing to activate or tab to. */}
      <div
        className="progress-panel-track"
        role="progressbar"
        aria-label={progressLabel}
        aria-valuenow={rounded}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div className="progress-panel-fill" style={{ width: `${rounded}%` }} />
      </div>
      <p className="progress-panel-pct">{rounded}%</p>
    </div>
  )
}
